import { NodeData } from '../interfaces/NodeData';
import { HotspotDescription } from '../interfaces/HotspotDescription';

export default class NetworkCalls {
  public static async fetchSurveyNodes(
    floor: number,
    date: Date,
    abortController: AbortController,
  ): Promise<NodeData[]> {
    return await fetch(process.env.REACT_APP_BASE_URL + '/api/survey/details?floor=' + floor + '&date=' + date.toISOString(),
    { signal: abortController.signal })
    .then(res => res.json())
    .then(body => {
      if(body.success){
        return body.payload;
      } else {
        return []
      }
    })
    .catch(e => console.log(e));
  }

  public static async fetchSurveys(
    abortController: AbortController,
    floor?: number,
  ): Promise<any[]> {

    const res: {
      success: boolean;
      payload: any[];
    } = await (
      await fetch(
        process.env.REACT_APP_BASE_URL + `/api/survey/details/compact${floor ? `?floor=${floor}` : ''}`,
        { signal: abortController.signal },
      )
    ).json();

    if (!res.success) {
      return [];
    }

    const surveys = res.payload
      .map((survey: any) => {
        return {
          date: new Date(survey.date),
          surveyName: survey.surveyName,
        };
      })
      .sort((one: any, two: any) => {
        if (one.date.getTime() === two.date.getTime()) {
          return -1 * one.surveyName.localeCompare(two.surveyName);
        } else {
          return one.date.getTime() <= two.date.getTime() ? 1 : -1;
        }
      });

    const groupedSurveys: any[] = [];

    surveys.forEach((val: any) => {
      const monthName = val.date.toLocaleString('en-us', { month: 'short', year: 'numeric' });
      if (groupedSurveys.length > 0 && monthName === groupedSurveys[groupedSurveys.length - 1].monthName) {
        groupedSurveys[groupedSurveys.length - 1].dates.push(val);
      } else {
        groupedSurveys[groupedSurveys.length] = {
          dates: [val],
          monthName,
        };
      }
    });

    if (abortController.signal.aborted) {
      return [];
    }

    return groupedSurveys;
  }

  public static async fetchAllSurveys(
    abortController: AbortController
  ): Promise<any[]> {

    const res: {
      success: boolean;
      payload: any[];
    } = await (
      await fetch(
        process.env.REACT_APP_BASE_URL + '/api/survey/details/compact',
        { signal: abortController.signal },
      )
    ).json();

    if (!res.success) {
      return [];
    }

    const surveys = res.payload
      .map((survey: any) => {
        return {
          date: new Date(survey.date),
          surveyName: survey.surveyName,
        };
      })
      .sort((one: any, two: any) => {
        if (one.date.getTime() === two.date.getTime()) {
          return -1 * one.surveyName.localeCompare(two.surveyName);
        } else {
          return one.date.getTime() <= two.date.getTime() ? 1 : -1;
        }
      });

    const groupedSurveys: any[] = [];

    surveys.forEach((val: any) => {
      const monthName = val.date.toLocaleString('en-us', { month: 'short', year: 'numeric' });
      if (groupedSurveys.length > 0 && monthName === groupedSurveys[groupedSurveys.length - 1].monthName) {
        groupedSurveys[groupedSurveys.length - 1].dates.push(val);
      } else {
        groupedSurveys[groupedSurveys.length] = {
          dates: [val],
          monthName,
        };
      }
    });

    if (abortController.signal.aborted) {
      return [];
    }

    return groupedSurveys;
  }

  public static async fetchHotspotDescription(tilesId: string, abortController: AbortController): Promise<HotspotDescription[]> {
    const res: {
      success: boolean;
      payload: HotspotDescription[];
    } = await (
      await fetch(
        process.env.REACT_APP_BASE_URL + '/api/hotspot/details?tilesId=' + tilesId,
        { signal: abortController.signal },
      )
    ).json();

    if (!res.success) {
      return [];
    }

    return res.payload;
  }

  public static async fetchMinimapImage(floor: number,
    abortController: AbortController): Promise<string> {

    const res: {
      success: boolean;
      payload: any;
    } = await (
      await fetch(
        process.env.REACT_APP_BASE_URL + '/api/minimap/details?floor=' + floor,
        { signal: abortController.signal },
      )
    ).json();

    if (!res.success) {
      return "";
    }
    return res.payload;
  }

  public static async fetchFiles(id: string, abortController: AbortController):
    Promise<any[]> {
    const res: {
      success: boolean;
      payload: any;
    } = await (
      await fetch(process.env.REACT_APP_BASE_URL + '/api/documentation/details?_id=' + id,
        { signal: abortController.signal },
      )
    ).json();

    if (!res.success) {
      return [];
    }
    return res.payload;
  }

  public static async fetchAllFiles(abortController: AbortController):
  Promise<any[]> {
  const res: {
    success: boolean;
    payload: any;
  } = await (
    await fetch(process.env.REACT_APP_BASE_URL + '/api/documentation',
      { signal: abortController.signal },
    )
  ).json();

  if (!res.success) {
    return [];
  }
  return res.payload;
}

  public static async fetchDirectoryFromId(id: string, abortController: AbortController):
    Promise<any[]> {

    const res: {
      success: boolean;
      payload: any;
    } = await (
      await fetch(process.env.REACT_APP_BASE_URL +
        '/api/directories/details?_id=' + id,
        { signal: abortController.signal },
      )
    ).json();

    if (!res.success) {
      return [];
    }
    return res.payload;
  }

  public static async fetchDirectory(abortController: AbortController, directoryName?: string):
    Promise<any[]> {
    const res: {
      success: boolean;
      payload: any;
    } = await (
      await fetch(
        directoryName === undefined ?
          (process.env.REACT_APP_BASE_URL + '/api/directories/root') :
          (process.env.REACT_APP_BASE_URL + '/api/directories/details?name=' +
            directoryName),
        { signal: abortController.signal },
      )
    ).json();

    if (!res.success) {
      return [];
    }
    return res.payload;
  }

  public static async fetchAboutInfo(abortController: AbortController):
    Promise<any[]> {
    const res: {
      success: boolean;
      payload: any;
    } = await (
      await fetch(
        (process.env.REACT_APP_BASE_URL + '/api/about'),
        { signal: abortController.signal },
      )
    ).json();

    if (!res.success) {
      return [];
    }
    return res.payload;
  }
}
